import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  Cpu, Flame, MessageSquare, Users, ArrowRight, Activity,
  Gauge, Wrench, ChevronRight, Shield, UserCheck, MapPin, Lock, CreditCard, Award, MailOpen, CheckCircle2,
} from "lucide-react";
import LaunchPromoBanner from "@/components/LaunchPromoBanner";
import GooglePlayTeaser from "@/components/GooglePlayTeaser";
import GooglePlayLaunchBanner from "@/components/GooglePlayLaunchBanner";
import HallOfFameSection from "@/components/HallOfFameSection";
import InstallAppButton from "@/components/InstallAppButton";
import { HeroIsoDemo } from "@/components/HeroIsoDemo";

// Premium asset set
const HERO_BLUEPRINT = "https://static.prod-images.emergentagent.com/jobs/51c27263-25dc-4f0a-a858-db6e64a15193/images/94d9fc09fdeab24ac0db8a5dcf0ce51e22af198cafbf3495509b4fc8d3667079.png";
const TIG_MACRO = "https://static.prod-images.emergentagent.com/jobs/51c27263-25dc-4f0a-a858-db6e64a15193/images/c23dcbd239da3ccaacb62a3cf2edc182e95018291d464b60034b471c6d182212.png";
// Close-up of an automated orbital weld-head machine (no human welder)
const ORBITAL_DARK = "https://images.unsplash.com/photo-1657897392470-2fbc02127098?fm=jpg&q=80&w=1400&auto=format&fit=crop";
// Mirror-polished stainless steel pipe close-up (high-tech, pharma/food grade)
const PIPES_INOX   = "https://images.unsplash.com/photo-1705342348652-78a919f95e61?fm=jpg&q=80&w=1400&auto=format&fit=crop";
const WELDER_SPARKS = "https://images.unsplash.com/photo-1665494126333-ce412a7b07a9?fm=jpg&q=80&w=1600&auto=format&fit=crop";

// persona backgrounds (each with overlay)
const PERSONAS_MEDIA = [TIG_MACRO, ORBITAL_DARK, PIPES_INOX, HERO_BLUEPRINT];
// persona → blog slug (4th = pripravári -> shared pipefitter blog)
const PERSONAS_SLUG = ["tig", "orbital", "pipefitter", "pipefitter"];

// Per-language micro-strings for Landing-only copy.
const L = {
  sk: {
    signedIn: "Prihlásený / á",
    heroPunch: <>Spájame profesionálov. <span className="wb-gold-text">Doma.</span> V Európe. Vo svete.</>,
    heroItalic: <>Tigik nie je app pre každého — je pre tých, čo <span className="text-gold-400 font-bold not-italic">vedia, čo robia</span>.</>,
    joinPros: "Pridaj sa medzi profesionálov",
    openCommunity: "Otvoriť komunitu",
    inbox: "Schránka správ",
    inboxNew: (n) => `${n} ${n === 1 ? "nová správa" : n < 5 ? "nové správy" : "nových správ"}`,
    freeBannerTitle: "Registrácia ZADARMO · bez kreditky · 30 sekúnd",
    freeBannerSub: "FREE účet — zadarmo navždy:",
    free1: "Zváracie kalkulátory TIG, orbitál, potrubár",
    free2: "AI poradca Sauberino 3× denne",
    free3: "AI Izometria — 3D z výkresu 1× týždenne",
    free4: "Komunita zváračov zo 6 krajín",
    heroTitle1: "Appka pre zváračov a potrubárov:",
    heroTitle2: "kalkulátory, AI a 3D izometria",
    demoCaption: "Ukážka AI Izometrie · DN150 z ručného výkresu · otoč a priblíž prstami",
    demoCta: "Vyskúšaj na svojom výkrese →",
    freeBannerCta: "Vytvoriť FREE účet →",
    readyToElevate: "Pripravený zlepšiť svoj zvar?",
    days: "dni", week: "týždeň", month: "mesiac",
    plansBtn: "Plány",
    storyCta: "čítať príbeh →",
    certBannerTag: "ELITNÝ KLUB",
    certBannerTxt: "Máš certifikát SauberEko®?",
    certBannerCta: "PRO zdarma NATRVALO →",
    trainingTag: "🏗️ Tigik Tréningové centrum",
    trainingTxt: "Podpor Tigik Tréningové centrum v profile a tvoje meno bude navždy v Sieni slávy ako podporovateľ centra.",
    trainingCta: "Podporiť centrum →",
  },
  en: {
    signedIn: "Signed in",
    heroPunch: <>Connecting professionals. <span className="wb-gold-text">At home.</span> In Europe. Worldwide.</>,
    heroItalic: <>Tigik is not an app for everyone — it&apos;s for those who <span className="text-gold-400 font-bold not-italic">know what they&apos;re doing</span>.</>,
    joinPros: "Join the professionals",
    openCommunity: "Open community",
    inbox: "Inbox",
    inboxNew: (n) => `${n} new message${n === 1 ? "" : "s"}`,
    freeBannerTitle: "Sign up FREE · no credit card · 30 seconds",
    freeBannerSub: "FREE account — free forever:",
    free1: "Welding calculators: TIG, orbital, pipefitter",
    free2: "AI advisor Sauberino 3× a day",
    free3: "AI Isometric — 3D from a drawing 1× a week",
    free4: "Welders community from 6 countries",
    heroTitle1: "The app for welders & pipefitters:",
    heroTitle2: "calculators, AI and 3D isometrics",
    demoCaption: "AI Isometric demo · DN150 from a hand drawing · rotate and pinch to zoom",
    demoCta: "Try it on your drawing →",
    freeBannerCta: "Create FREE account →",
    readyToElevate: "Ready to elevate your weld?",
    days: "days", week: "week", month: "month",
    plansBtn: "Plans",
    storyCta: "read the story →",
    certBannerTag: "ELITE CLUB",
    certBannerTxt: "Hold a SauberEko® certificate?",
    certBannerCta: "PRO free for life →",
    trainingTag: "🏗️ Tigik Training Centre",
    trainingTxt: "Support the Tigik Training Centre in your profile and your name will stay forever in the Hall of Fame as a centre supporter.",
    trainingCta: "Support the centre →",
  },
  cs: {
    signedIn: "Přihlášen / a",
    heroPunch: <>Spojujeme profesionály. <span className="wb-gold-text">Doma.</span> V Evropě. Ve světě.</>,
    heroItalic: <>Tigik není app pro každého — je pro ty, kteří <span className="text-gold-400 font-bold not-italic">vědí, co dělají</span>.</>,
    joinPros: "Přidej se mezi profesionály",
    openCommunity: "Otevřít komunitu",
    inbox: "Schránka zpráv",
    inboxNew: (n) => `${n} ${n === 1 ? "nová zpráva" : n < 5 ? "nové zprávy" : "nových zpráv"}`,
    freeBannerTitle: "Registrace ZDARMA · bez karty · 30 sekund",
    freeBannerSub: "FREE účet — zdarma navždy:",
    free1: "Svářečské kalkulačky TIG, orbitál, potrubář",
    free2: "AI poradce Sauberino 3× denně",
    free3: "AI Izometrie — 3D z výkresu 1× týdně",
    free4: "Komunita svářečů ze 6 zemí",
    heroTitle1: "Appka pro svářeče a potrubáře:",
    heroTitle2: "kalkulačky, AI a 3D izometrie",
    demoCaption: "Ukázka AI Izometrie · DN150 z ručního výkresu · otoč a přibliž prsty",
    demoCta: "Vyzkoušej na svém výkresu →",
    freeBannerCta: "Vytvořit FREE účet →",
    readyToElevate: "Připraven zlepšit svůj svar?",
    days: "dny", week: "týden", month: "měsíc",
    plansBtn: "Plány",
    storyCta: "číst příběh →",
    certBannerTag: "ELITNÍ KLUB",
    certBannerTxt: "Máš certifikát SauberEko®?",
    certBannerCta: "PRO zdarma NATRVALO →",
    trainingTag: "🏗️ Tigik Tréninkové centrum",
    trainingTxt: "Podpoř Tigik Tréninkové centrum v profilu a tvé jméno bude navždy v Síni slávy jako podporovatel centra.",
    trainingCta: "Podpořit centrum →",
  },
  de: {
    signedIn: "Angemeldet",
    heroPunch: <>Wir verbinden Profis. <span className="wb-gold-text">Zu Hause.</span> In Europa. Weltweit.</>,
    heroItalic: <>Tigik ist keine App für jeden — sie ist für die, die <span className="text-gold-400 font-bold not-italic">wissen, was sie tun</span>.</>,
    joinPros: "Werde Teil der Profis",
    openCommunity: "Community öffnen",
    inbox: "Posteingang",
    inboxNew: (n) => `${n} neue Nachricht${n === 1 ? "" : "en"}`,
    freeBannerTitle: "KOSTENLOS registrieren · keine Kreditkarte · 30 Sek.",
    freeBannerSub: "FREE-Konto — für immer kostenlos:",
    free1: "Schweißrechner: WIG, Orbital, Rohrleitungsbau",
    free2: "KI-Berater Sauberino 3× täglich",
    free3: "KI-Isometrie — 3D aus der Zeichnung 1× pro Woche",
    free4: "Schweißer-Community aus 6 Ländern",
    heroTitle1: "Die App für Schweißer & Rohrleitungsbauer:",
    heroTitle2: "Rechner, KI und 3D-Isometrie",
    demoCaption: "KI-Isometrie-Demo · DN150 aus einer Handskizze · drehen und zoomen mit den Fingern",
    demoCta: "Mit deiner Zeichnung testen →",
    freeBannerCta: "FREE Konto erstellen →",
    readyToElevate: "Bereit, deine Naht zu verbessern?",
    days: "Tage", week: "Woche", month: "Monat",
    plansBtn: "Pläne",
    storyCta: "Geschichte lesen →",
    certBannerTag: "ELITE-CLUB",
    certBannerTxt: "Hast du ein SauberEko®-Zertifikat?",
    certBannerCta: "PRO auf Lebenszeit gratis →",
    trainingTag: "🏗️ Tigik Trainingszentrum",
    trainingTxt: "Unterstütze das Tigik Trainingszentrum in deinem Profil und dein Name bleibt für immer in der Ruhmeshalle als Unterstützer des Zentrums.",
    trainingCta: "Zentrum unterstützen →",
  },
  pl: {
    signedIn: "Zalogowano",
    heroPunch: <>Łączymy profesjonalistów. <span className="wb-gold-text">W kraju.</span> W Europie. Na świecie.</>,
    heroItalic: <>Tigik to nie aplikacja dla każdego — to aplikacja dla tych, którzy <span className="text-gold-400 font-bold not-italic">wiedzą co robią</span>.</>,
    joinPros: "Dołącz do profesjonalistów",
    openCommunity: "Otwórz społeczność",
    inbox: "Skrzynka",
    inboxNew: (n) => `${n} ${n === 1 ? "nowa wiadomość" : "nowych wiadomości"}`,
    freeBannerTitle: "Rejestracja DARMOWA · bez karty · 30 sekund",
    freeBannerSub: "Konto FREE — darmowe na zawsze:",
    free1: "Kalkulatory spawalnicze TIG, orbital, rurociągi",
    free2: "Doradca AI Sauberino 3× dziennie",
    free3: "AI Izometria — 3D z rysunku 1× w tygodniu",
    free4: "Społeczność spawaczy z 6 krajów",
    heroTitle1: "Aplikacja dla spawaczy i monterów rurociągów:",
    heroTitle2: "kalkulatory, AI i izometria 3D",
    demoCaption: "Demo AI Izometrii · DN150 z odręcznego rysunku · obróć i przybliż palcami",
    demoCta: "Wypróbuj na swoim rysunku →",
    freeBannerCta: "Utwórz DARMOWE konto →",
    readyToElevate: "Gotowy podnieść jakość swojej spoiny?",
    days: "dni", week: "tydzień", month: "miesiąc",
    plansBtn: "Plany",
    storyCta: "czytaj historię →",
    certBannerTag: "KLUB ELITY",
    certBannerTxt: "Masz certyfikat SauberEko®?",
    certBannerCta: "PRO za darmo NA ZAWSZE →",
    trainingTag: "🏗️ Centrum Szkoleniowe Tigik",
    trainingTxt: "Wesprzyj Centrum Szkoleniowe Tigik w swoim profilu, a twoje imię na zawsze pozostanie w Galerii Sław jako wspierający centrum.",
    trainingCta: "Wesprzyj centrum →",
  },
  fr: {
    signedIn: "Connecté(e)",
    heroPunch: <>On connecte les pros. <span className="wb-gold-text">Chez toi.</span> En Europe. Dans le monde.</>,
    heroItalic: <>Tigik n'est pas une app pour tout le monde — elle est pour ceux qui <span className="text-gold-400 font-bold not-italic">savent ce qu'ils font</span>.</>,
    joinPros: "Rejoins les pros",
    openCommunity: "Ouvrir la communauté",
    inbox: "Boîte de réception",
    inboxNew: (n) => `${n} ${n === 1 ? "nouveau message" : "nouveaux messages"}`,
    freeBannerTitle: "Inscription GRATUITE · sans carte bancaire · 30 secondes",
    freeBannerSub: "Compte FREE — gratuit pour toujours :",
    free1: "Calculateurs de soudage TIG, orbital, tuyauterie",
    free2: "Conseiller IA Sauberino 3× par jour",
    free3: "IA Isométrie — 3D depuis un plan 1× par semaine",
    free4: "Communauté de soudeurs de 6 pays",
    heroTitle1: "L'appli des soudeurs et tuyauteurs :",
    heroTitle2: "calculateurs, IA et isométrie 3D",
    demoCaption: "Démo IA Isométrie · DN150 depuis un croquis · fais pivoter et zoome du doigt",
    demoCta: "Essaie avec ton plan →",
    freeBannerCta: "Créer un compte FREE →",
    readyToElevate: "Prêt à améliorer ta soudure ?",
    days: "jours", week: "semaine", month: "mois",
    plansBtn: "Formules",
    storyCta: "lire l'histoire →",
    certBannerTag: "CLUB ÉLITE",
    certBannerTxt: "Tu as un certificat SauberEko® ?",
    certBannerCta: "PRO gratuit À VIE →",
    trainingTag: "🏗️ Centre de formation Tigik",
    trainingTxt: "Soutiens le Centre de formation Tigik depuis ton profil et ton nom restera pour toujours au Hall of Fame comme soutien du centre.",
    trainingCta: "Soutenir le centre →",
  },
};

export default function Landing() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const l = L[lang] || L.sk;
  const [unreadDM, setUnreadDM] = React.useState(0);

  // Fetch unread DM count for the hero "Inbox" CTA badge (logged-in users only)
  React.useEffect(() => {
    if (!user) { setUnreadDM(0); return; }
    let alive = true;
    const fetchCount = async () => {
      try {
        const r = await api.get("/dm/unread-count");
        if (alive) setUnreadDM(r.data?.count || 0);
      } catch { /* silent */ }
    };
    fetchCount();
    // Refresh every 30s while on landing
    const interval = setInterval(fetchCount, 30000);
    return () => { alive = false; clearInterval(interval); };
  }, [user]);

  return (
    <div data-testid="landing-page" className="relative">
      {/* CELEBRATION BANNER — Tigik LIVE on Google Play! 🎉 */}

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-zinc-800">
        {/* Composite "shadow" hero: 4 persona images blended into a single dark tableau */}
        <div className="absolute inset-0 grid grid-cols-2 grid-rows-2" aria-hidden="true">
          {[TIG_MACRO, ORBITAL_DARK, PIPES_INOX, HERO_BLUEPRINT].map((src, i) => (
            <div
              key={i}
              className="relative overflow-hidden"
              style={{
                backgroundImage: `url(${src})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                filter: "grayscale(60%) brightness(0.42) contrast(1.05)",
                mixBlendMode: "screen",
                opacity: 0.55,
              }}
            />
          ))}
        </div>
        {/* Gold light & vignette overlays — bind the 4 frames into one cohesive shadow */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(900px 600px at 22% 38%, rgba(212,175,55,0.18), transparent 70%), radial-gradient(900px 600px at 80% 60%, rgba(212,175,55,0.10), transparent 70%)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/72 to-[var(--wb-bg)]" />
        <div className="absolute inset-0 wb-grid-bg opacity-20" />
        {/* Seam fade — hides the 2x2 grid lines so the 4 images read as one image */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, transparent 45%, rgba(0,0,0,0.85) 50%, transparent 55%), linear-gradient(0deg, transparent 45%, rgba(0,0,0,0.85) 50%, transparent 55%)",
            filter: "blur(40px)",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 wb-fade-in">
            {user && (
              <div data-testid="welcome-banner" className="inline-flex items-center gap-3 mb-6 px-4 py-2 bg-black/60 backdrop-blur border border-gold-700/50">
                <div className="w-9 h-9 bg-gold-600/20 border border-gold-600 flex items-center justify-center">
                  <UserCheck className="w-4 h-4 text-gold-400" />
                </div>
                <div className="leading-tight">
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">{l.signedIn}</div>
                  <div className="font-display font-bold text-base text-white flex items-center gap-2">
                    {user.name}
                    {user.role === "admin"
                      ? <span className="wb-tag !text-gold-300 !border-gold-500 inline-flex items-center gap-1"><Shield className="w-3 h-3" /> ADMIN</span>
                      : user.is_subscribed
                        ? <span className="wb-tag !text-gold-400 !border-gold-600">{
                            user.plan_tier === "monthly" ? "PRO"
                              : user.plan_tier === "weekly" ? "ACTIVE"
                              : user.plan_tier === "start" ? "START"
                              : (user.plan_tier || "PRO").toUpperCase()
                          }</span>
                        : <span className="wb-tag">FREE</span>}
                  </div>
                </div>
              </div>
            )}
            {/* Tigik brand pill */}
            <Link
              to="#"
              data-testid="saubereko-badge"
              className="inline-flex items-center gap-2 px-3 py-1.5 mb-6 border border-gold-700/50 bg-black/60 backdrop-blur"
            >
              <img src="/tigik-logo.png" alt="Tigik" className="w-5 h-5 object-cover" />
              <span className="text-[10px] md:text-xs font-mono uppercase tracking-[0.2em] text-gold-400">
                <span className="font-display font-bold tracking-tight text-gold-300">Tigik</span> · by SauberEko®
              </span>
            </Link>

            <div className="flex items-center gap-2 mb-4">
              <span className="wb-tag !border-gold-600 !text-gold-400">
                <span className="w-1.5 h-1.5 bg-gold-500 rounded-full wb-pulse" /> {t.landing.tag}
              </span>
            </div>
            <h1 data-testid="hero-title" className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter">
              {l.heroTitle1}<br />
              <span className="wb-gold-text">{l.heroTitle2}</span>
            </h1>

            <HeroIsoDemo caption={l.demoCaption} cta={l.demoCta} to={user ? "/pipe-visualizer" : "/register"} />

            <div className="mt-6 flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4" data-testid="hero-ctas">
              {!user && (
                <Link to="/register" data-testid="hero-cta-primary"
                  className="wb-btn-primary px-8 py-4 inline-flex items-center justify-center gap-2 text-sm">
                  {l.joinPros} <ArrowRight className="w-4 h-4" />
                </Link>
              )}
              {user && (
                <Link to="/community" data-testid="hero-cta-primary"
                  className="wb-btn-primary px-8 py-4 inline-flex items-center justify-center gap-2 text-sm">
                  {l.openCommunity} <ArrowRight className="w-4 h-4" />
                </Link>
              )}
              {/* Logged-in secondary CTA: Inbox with unread badge. Guests still get the tour. */}
              {user ? (
                <Link to="/profile" data-testid="hero-cta-inbox"
                  className="wb-btn-ghost px-8 py-4 inline-flex items-center gap-2 text-sm relative">
                  <MailOpen className="w-4 h-4" />
                  {unreadDM > 0 ? l.inboxNew(unreadDM) : l.inbox}
                  {unreadDM > 0 && (
                    <span className="ml-1 min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center text-[10px] font-bold bg-red-600 text-white rounded-full animate-pulse">
                      {unreadDM > 99 ? "99+" : unreadDM}
                    </span>
                  )}
                </Link>
              ) : (
                <InstallAppButton big className="!px-8 !py-4 !text-sm justify-center" />
              )}
              {!user && (
                <Link to="/tour" data-testid="hero-cta-secondary"
                  className="wb-btn-ghost px-8 py-4 inline-flex items-center justify-center gap-2 text-sm">
                  {t.landing.ctaSecondary} <ChevronRight className="w-4 h-4" />
                </Link>
              )}
            </div>

            {/* Community + exclusivity slogan (smaller, under the demo) */}
            <div className="mt-8 border-l-2 border-gold-500 pl-5 max-w-xl">
              <p className="font-display font-bold text-base md:text-lg leading-tight text-white">
                {l.heroPunch}
              </p>
              <p className="text-xs md:text-sm text-zinc-400 mt-2 leading-relaxed italic">
                {l.heroItalic}
              </p>
            </div>

            <p className="mt-6 text-base md:text-lg text-zinc-400 max-w-xl leading-relaxed">
              {t.landing.sub}
            </p>
            {/* FREE registration benefits — only for guests */}
            {!user && (
              <Link
                to="/register"
                data-testid="landing-free-banner"
                className="mt-8 block border border-gold-700/40 bg-gradient-to-br from-zinc-950 via-black to-gold-950/20 p-5 hover:border-gold-500 hover:bg-gold-950/30 transition-all max-w-2xl"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center gap-1.5 text-[9px] sm:text-[10px] font-mono uppercase tracking-[0.2em] text-black bg-gold-400 px-2.5 py-1 font-bold">
                    ✦ {l.freeBannerTitle}
                  </span>
                </div>
                <p className="text-sm text-zinc-300 font-display mb-3">
                  {l.freeBannerSub}
                </p>
                <ul className="grid sm:grid-cols-2 gap-x-5 gap-y-2 text-sm text-zinc-300 mb-4" data-testid="landing-free-list">
                  <li className="inline-flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-gold-400 flex-shrink-0" /> {l.free1}</li>
                  <li className="inline-flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-gold-400 flex-shrink-0" /> {l.free2}</li>
                  <li className="inline-flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-gold-400 flex-shrink-0" /> {l.free3}</li>
                  <li className="inline-flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-gold-400 flex-shrink-0" /> {l.free4}</li>
                </ul>
                <div className="flex items-center justify-between gap-3 pt-3 border-t border-zinc-800/60">
                  <span className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider">Upgrade kedykoľvek na START / ACTIVE / PRO</span>
                  <span className="text-xs font-mono uppercase tracking-widest text-gold-300 group-hover:text-gold-200 font-bold inline-flex items-center gap-1.5">
                    {l.freeBannerCta}
                  </span>
                </div>
              </Link>
            )}

            {/* Iteration 79 — Training Center promo strip (always visible) */}
            <Link
              to="/profile#support"
              data-testid="landing-training-banner"
              className="mt-5 group flex items-start gap-3 px-4 py-3 border border-amber-500/50 bg-gradient-to-r from-amber-950/40 via-black to-amber-950/40 hover:border-amber-400 transition-colors max-w-2xl"
              style={{ boxShadow: "0 6px 24px -10px rgba(212,175,55,0.35)" }}
            >
              <span className="flex-shrink-0 w-9 h-9 bg-gradient-to-br from-amber-500 to-amber-700 border border-amber-400 flex items-center justify-center text-lg" style={{ borderRadius: "50%" }} aria-hidden>
                🏗️
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-mono uppercase tracking-widest text-amber-300 leading-snug">{l.trainingTag}</div>
                <div className="text-xs sm:text-sm text-white leading-snug mt-0.5">{l.trainingTxt}</div>
              </div>
              <span className="text-[11px] font-bold text-amber-400 flex-shrink-0 self-center group-hover:text-amber-300 hidden sm:inline">{l.trainingCta}</span>
            </Link>

            {/* Mobile-only thin certified-holder banner (desktop version sits above mosaic) */}
            <Link
              to="/pricing"
              data-testid="landing-cert-thin-banner-mobile"
              className="lg:hidden group relative flex items-center gap-2.5 mt-5 px-3 py-2.5 border border-gold-600 bg-gradient-to-r from-gold-500/10 via-gold-500/15 to-gold-500/10 overflow-hidden"
              style={{ boxShadow: "0 6px 24px -10px rgba(212,175,55,0.4)" }}
            >
              <span className="flex-shrink-0 w-7 h-7 bg-gradient-to-br from-gold-400 to-gold-600 border border-gold-300 flex items-center justify-center" style={{ borderRadius: "50%" }}>
                <Award className="w-3.5 h-3.5 text-black" strokeWidth={2.4} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[9px] font-mono uppercase tracking-widest text-gold-300">{l.certBannerTag}</div>
                <div className="text-xs font-bold text-white leading-snug">{l.certBannerTxt}</div>
              </div>
              <span className="text-[11px] font-bold text-gold-400 flex-shrink-0">{l.certBannerCta}</span>
            </Link>

            {/* Trust strip — Slovak origin + security + GDPR */}
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">
              <span className="inline-flex items-center gap-1.5"><MapPin className="w-3 h-3 text-gold-500" /> Made in Slovakia 🇸🇰</span>
              <span className="text-zinc-800">·</span>
              <span className="inline-flex items-center gap-1.5"><Lock className="w-3 h-3 text-gold-500" /> GDPR ready</span>
              <span className="text-zinc-800">·</span>
              <span className="inline-flex items-center gap-1.5"><CreditCard className="w-3 h-3 text-gold-500" /> Stripe secured</span>
            </div>

            <div className="mt-8 max-w-xl flex flex-col sm:flex-row gap-3 sm:items-center">
              <LaunchPromoBanner />
            </div>

            <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl">
              {[
                { v: t.landing.stat1v, k: t.landing.stat1 },
                { v: t.landing.stat2v, k: t.landing.stat2 },
                { v: t.landing.stat3v, k: t.landing.stat3 },
                { v: t.landing.stat4v, k: t.landing.stat4 },
              ].map((s, i) => (
                <div key={i} className="border-l-2 border-gold-500 pl-3">
                  <div className="font-display font-black text-3xl text-white">{s.v}</div>
                  <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">{s.k}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Hero MIX mosaic */}
          <div className="lg:col-span-5 relative hidden lg:block wb-fade-in" style={{ animationDelay: "0.2s" }}>
            {/* Thin certified-holder banner above mosaic — eye-catching but compact */}
            <Link
              to="/pricing"
              data-testid="landing-cert-thin-banner"
              className="group relative flex items-center gap-2.5 mb-3 px-3 py-2 border border-gold-600 bg-gradient-to-r from-gold-500/10 via-gold-500/15 to-gold-500/10 hover:from-gold-500/20 hover:to-gold-500/20 transition-colors overflow-hidden"
              style={{ boxShadow: "0 6px 24px -10px rgba(212,175,55,0.4)" }}
            >
              {/* shine glide */}
              <span className="absolute inset-y-0 left-0 w-1/3 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity" style={{
                background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.08) 50%, transparent 100%)",
              }} />
              <span className="flex-shrink-0 w-6 h-6 bg-gradient-to-br from-gold-400 to-gold-600 border border-gold-300 flex items-center justify-center" style={{ borderRadius: "50%" }}>
                <Award className="w-3 h-3 text-black" strokeWidth={2.4} />
              </span>
              <span className="text-[9px] font-mono uppercase tracking-widest text-gold-300 hidden xl:inline">{l.certBannerTag}</span>
              <span className="text-xs font-bold text-white flex-1 min-w-0 truncate">{l.certBannerTxt}</span>
              <span className="text-[11px] font-bold text-gold-400 flex-shrink-0 group-hover:text-gold-300 transition-colors">{l.certBannerCta}</span>
            </Link>

            <div className="relative grid grid-cols-2 grid-rows-2 gap-2 aspect-square">
              <div className="relative overflow-hidden border border-gold-700/40">
                <img src={TIG_MACRO} alt="TIG" className="w-full h-full object-cover" />
                <span className="absolute bottom-2 left-2 wb-tag !text-gold-300 !border-gold-600 backdrop-blur bg-black/60">TIG</span>
              </div>
              <div className="relative overflow-hidden border border-gold-700/40">
                <img src={ORBITAL_DARK} alt="Orbital" className="w-full h-full object-cover" />
                <span className="absolute bottom-2 left-2 wb-tag !text-gold-300 !border-gold-600 backdrop-blur bg-black/60">ORBITAL</span>
              </div>
              <div className="relative overflow-hidden border border-gold-700/40">
                <img src={PIPES_INOX} alt="Inox pipes" className="w-full h-full object-cover" />
                <span className="absolute bottom-2 left-2 wb-tag !text-gold-300 !border-gold-600 backdrop-blur bg-black/60">INOX</span>
              </div>
              <div className="relative overflow-hidden border border-gold-700/40">
                <img src={HERO_BLUEPRINT} alt="Blueprint" className="w-full h-full object-cover" />
                <span className="absolute bottom-2 left-2 wb-tag !text-gold-300 !border-gold-600 backdrop-blur bg-black/60">BLUEPRINT</span>
              </div>
            </div>
            {/* Live-calc overlay */}
            <div className="absolute -bottom-4 -left-4 bg-[var(--wb-surface)] border border-gold-700/50 p-4 w-64 shadow-2xl">
              <div className="flex items-center justify-between mb-2">
                <span className="wb-tag !text-gold-400 !border-gold-600">LIVE CALC</span>
                <Activity className="w-3.5 h-3.5 text-gold-500" />
              </div>
              <div className="font-mono text-xs text-zinc-400 space-y-1">
                <div className="flex justify-between"><span>I:</span><span className="text-white">142.0 A</span></div>
                <div className="flex justify-between"><span>v:</span><span className="text-white">98 mm/min</span></div>
                <div className="flex justify-between"><span>Ar:</span><span className="text-white">12 l/min</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HALL OF FAME — FIRST after hero, social proof + prestige */}
      <HallOfFameSection />

      {/* FEATURES */}
      <GooglePlayLaunchBanner />

      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="features-section">
        <div className="grid md:grid-cols-2 gap-px bg-zinc-800">
          {[
            { icon: Gauge, title: t.landing.f1Title, desc: t.landing.f1Desc, to: "/calculators/orbital", tag: "ORBITAL" },
            { icon: Wrench, title: t.landing.f2Title, desc: t.landing.f2Desc, to: "/calculators/manual-tig", tag: "TIG" },
            { icon: Cpu, title: t.landing.f3Title, desc: t.landing.f3Desc, to: "/ai-advisor", tag: "AI" },
            { icon: Users, title: t.landing.f4Title, desc: t.landing.f4Desc, to: "/community", tag: "CHAT" },
          ].map((f, i) => (
            <Link to={f.to} key={i} data-testid={`feature-card-${i}`}
              className="group bg-[var(--wb-bg)] p-8 md:p-10 hover:bg-[var(--wb-surface)] transition-colors relative">
              <div className="flex items-start justify-between mb-6">
                <f.icon className="w-8 h-8 text-gold-500" strokeWidth={1.5} />
                <span className="wb-tag">{f.tag}</span>
              </div>
              <h3 className="font-display font-bold text-2xl mb-2 group-hover:text-gold-400 transition-colors">
                {f.title}
              </h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{f.desc}</p>
              <ArrowRight className="w-4 h-4 mt-6 text-zinc-600 group-hover:text-gold-500 transition-colors" />
            </Link>
          ))}
        </div>
      </section>

      {/* PERSONAS / WHO — each card with its own bg image */}
      <section className="relative border-t border-zinc-800" data-testid="personas-section">
        <div className="absolute inset-0">
          <img src={WELDER_SPARKS} alt="" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-24">
          <span className="wb-tag mb-4 !border-gold-700 !text-gold-400">{t.landing.sectionWho}</span>
          <h2 className="font-display font-black text-4xl md:text-5xl mt-3 mb-10 max-w-2xl">
            <span className="wb-gold-text">{t.landing.sectionWho}</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="personas-grid">
            {t.landing.personas.map((p, i) => (
              <Link key={i}
                to={`/blog/${PERSONAS_SLUG[i]}`}
                data-testid={`persona-${i}`}
                className="relative overflow-hidden border border-gold-700/30 h-48 group block">
                <img src={PERSONAS_MEDIA[i]} alt={p}
                  className="absolute inset-0 w-full h-full object-cover scale-110 group-hover:scale-100 transition-transform duration-700"
                  style={{ filter: "grayscale(40%) brightness(0.55) contrast(1.1)" }} />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="relative h-full flex flex-col justify-end p-5">
                  <Flame className="w-5 h-5 text-gold-400 mb-2" strokeWidth={1.5} />
                  <div className="font-display font-bold text-xl">{p}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-gold-500/80 mt-1">0{i + 1} · {l.storyCta}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HALL OF FAME — moved up; remove duplicate before Google Play teaser */}

      {/* Google Play teaser */}
      <GooglePlayTeaser />

      {/* CTA */}
      <section className="border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-20 text-center">
          <MessageSquare className="w-10 h-10 text-gold-500 mx-auto mb-6" strokeWidth={1.5} />
          <h2 className="font-display font-black text-4xl md:text-6xl tracking-tight">
            {l.readyToElevate}
          </h2>
          <p className="mt-4 text-zinc-400 max-w-xl mx-auto">{t.landing.sub}</p>
          <div className="mt-6 inline-flex flex-wrap items-center justify-center gap-3 text-sm font-mono uppercase tracking-wider text-zinc-300">
            <span className="wb-tag !border-zinc-700">START €1.99 / 2 {l.days}</span>
            <span className="text-zinc-700">·</span>
            <span className="wb-tag !border-gold-700 !text-gold-400">ACTIVE €3.99 / {l.week}</span>
            <span className="text-zinc-700">·</span>
            <span className="wb-tag !border-gold-600 !text-gold-300">PRO €6.99 / {l.month}</span>
          </div>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to={user ? "/community" : "/register"} data-testid="footer-cta"
              className="wb-btn-primary inline-flex items-center gap-2 px-8 py-4 text-sm">
              {user ? l.openCommunity : l.joinPros} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/pricing" data-testid="footer-cta-pricing"
              className="wb-btn-ghost inline-flex items-center gap-2 px-8 py-4 text-sm">
              {l.plansBtn} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <p className="mt-10 text-[11px] font-mono uppercase tracking-[0.25em] text-zinc-600 flex items-center justify-center gap-2">
            <img src="/tigik-logo.png" alt="Tigik" className="w-4 h-4 object-cover" />
            Tigik · by SauberEko® · tigik.saubereko.eu
          </p>
        </div>
      </section>
    </div>
  );
}
